import React from "react"
import { UseOptionsProps, UseOptionsReturn, UseSetDefaultValueProps } from "./DateInputTypes"
import { monthInfo, addLeadingZero, getMonth } from "./utils"

export function useOptions({ month, year, numberYearsSupported, startingYear }: UseOptionsProps): UseOptionsReturn {
  const monthOptions = React.useMemo(
    () =>
      monthInfo.map((monthData, index) => ({
        label: `${monthData.name} - ${addLeadingZero(index + 1)}`,
        value: index + 1,
        name: monthData.name
      })),
    []
  )

  const dayOptions = React.useMemo(
    () =>
      Array.from({ length: month ? getMonth(month?.value).getDays(year) : 31 }).map((_, index) => ({
        label: String(addLeadingZero(index + 1)),
        value: index + 1
      })),
    [month, year]
  )

  const yearOptions = React.useMemo(
    () =>
      Array.from({ length: numberYearsSupported }).map((_, index) => ({
        label: String(startingYear - index),
        value: startingYear - index
      })),
    [numberYearsSupported, startingYear]
  )

  return {
    dayOptions,
    monthOptions,
    yearOptions
  }
}

export function useSetDefaultValue({ defaultValue, setYear, setMonth, setDay }: UseSetDefaultValueProps): void {
  React.useEffect(() => {
    if (defaultValue) {
      const [defaultYear = "", defaultMonth = "", defaultDay = ""] = defaultValue.split("-")
      if (defaultYear.length === 4 && defaultMonth.length === 2 && defaultDay.length === 2) {
        const monthNumber = parseInt(defaultMonth, 10)
        const dayNumber = parseInt(defaultDay, 10)
        const yearNumber = parseInt(defaultYear, 10)

        const foundMonth = getMonth(monthNumber)
        const yearSelection = { label: String(yearNumber), value: yearNumber }
        let maxDaysInMonth = 31
        setYear(yearSelection)
        if (foundMonth) {
          setMonth({ label: foundMonth.name, value: monthNumber })
          maxDaysInMonth = foundMonth.getDays(yearSelection)
        }
        if (dayNumber <= maxDaysInMonth && dayNumber > 0) {
          setDay({ label: addLeadingZero(dayNumber), value: dayNumber })
        }
      }
    }
  }, [defaultValue, setDay, setMonth, setYear])
}
